import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, TextField, Button, Alert, Snackbar } from '@mui/material';
import { getOrgAdvertTemplate, saveOrgAdvertTemplate } from '../../services/api';
import Loader from '../../components/Common/Loader';

// Same drag & drop upload box used by Advert Library
const DropBox = ({ onFileSelected, file }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onFileSelected(dropped);
  };

  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <Box
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{
        width: 220, height: 220, border: '2px dashed #999', borderRadius: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', textAlign: 'center', p: 1,
        bgcolor: dragOver ? '#f0f0f0' : 'transparent',
        overflow: 'hidden'
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => e.target.files?.[0] && onFileSelected(e.target.files[0])}
      />
      {preview ? (
        <Box component="img" src={preview} alt="Preview" sx={{ maxWidth: '100%', maxHeight: '100%' }} />
      ) : (
        <Typography variant="h6" color="text.secondary" fontWeight={500}>
          CLICK<br />TO ENTER<br />FILE
        </Typography>
      )}
    </Box>
  );
};

const OrgAdvert = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [file, setFile] = useState(null);
  const [link, setLink] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOrgAdvertTemplate();
      if (res.success) {
        setTemplate(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load org advert template');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!file && !template?.image && !link.trim()) {
      setError('An image or a link is required');
      return;
    }
    setSaving(true);
    try {
      const res = await saveOrgAdvertTemplate({ file, link: link.trim() });
      setTemplate(res.data);
      // Reset back to the empty input screen — there's no preview to confirm against
      // here; the org checks a group's Live Adverts to see the advert is live.
      setFile(null);
      setLink('');
      const { successCount, totalGroups } = res.appliedTo || {};
      setSuccess(
        `Advert applied to ${successCount} of ${totalGroups} group(s). ` +
        `Open any group's Live Adverts to confirm it shows in position B.`
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save org advert template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="h4" fontWeight={600}>
          Org Advert
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drop an organisation-wide advert into position B for every group, on all apps.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 500 }}>
        {loading ? (
          <Loader sx={{ py: 3 }} />
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
              <DropBox file={file} onFileSelected={setFile} />
              <Typography variant="caption" color="text.secondary">
                Adverts with roughly equal width and height work best in the app.
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Link"
              placeholder="https://example.com"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              disabled={saving}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Applying…' : 'Apply to All Groups'}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={8000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrgAdvert;
